import React, { useState, useEffect } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button } from "reactstrap";
import Select from "react-select";
import { toast } from "react-toastify";
import API from "../../../services/API";

const AddTimetableModal = ({
    subjects,
    isOpen,
    onClose,
    classId,
    teachers,
    onAdded,
    selectedDay,
}) => {

    const [dayOfWeek, setDayOfWeek] = useState("");
    const [rotationCycle, setRotationCycle] = useState(1);
    const [rotationWeek, setRotationWeek] = useState(1);

    const [entries, setEntries] = useState([
        { subject_name: "", teacher_uuid: "", start_time: "", end_time: "", interval: 30, remarks: "" }
    ]);

    const [existing, setExisting] = useState([]);
    const [editingRows, setEditingRows] = useState({});
    const [adding, setAdding] = useState(false);

    const snapToInterval = (timeStr, interval) => {
        if (!timeStr) return "";
        let [h, m] = timeStr.split(":").map(Number);
        const total = h * 60 + m;
        const snapped = Math.round(total / interval) * interval;
        return `${String(Math.floor(snapped / 60)).padStart(2, "0")}:${String(snapped % 60).padStart(2, "0")}`;
    };

    const generateEndTime = (start, interval) => {
        if (!start) return "";
        let [h, m] = start.split(":").map(Number);
        const total = h * 60 + m + interval;
        return `${String(Math.floor(total / 60)).padStart(2, "0")}:${String(total % 60).padStart(2, "0")}`;
    };

    // Load day
    useEffect(() => {
        if (!isOpen) return;
        setDayOfWeek(selectedDay || "Monday");
    }, [isOpen]);

    useEffect(() => {
        if (isOpen) loadRotationInfo();
    }, [isOpen]);

    const loadRotationInfo = async () => {
        try {
            const token = localStorage.getItem("token");
            const res = await API.get("/timetable/rotation/info", {
                headers: { token, is_portal: true }
            });

            if (res.data.response_code === 1000) {
                setRotationCycle(res.data.data.rotation_cycle);
                setRotationWeek(1);
            }
        } catch (err) {
            console.error("Rotation info error:", err);
        }
    };

    // Load existing timetable
    useEffect(() => {
        if (isOpen) loadExisting();
    }, [isOpen, dayOfWeek, rotationWeek]);

    const loadExisting = async () => {
        try {
            const token = localStorage.getItem("token");

            const res = await API.get("/timetable/list", {
                headers: { token },
                params: { class_id: classId, is_portal: true }
            });

            if (res.data.response_code === 1000) {
                const list = res.data.data.filter(
                    (i) => i.day_of_week === dayOfWeek && i.rotation_week === rotationWeek
                );

                setExisting(list);

                const editMap = {};
                list.forEach(row => editMap[row.id] = { ...row });
                setEditingRows(editMap);
            }
        } catch (err) {
            console.error("Error loading existing", err);
        }
    };

    // Save new entries
    const handleSave = async () => {
        if (!dayOfWeek) return toast.error("Please select a day.");

        // Validate
        for (let e of entries) {
            if (!e.subject_name.trim()) return toast.error("Subject cannot be empty");
            if (!e.teacher_uuid) return toast.error("Teacher must be selected");
            if (!e.start_time) return toast.error("Start time cannot be empty");
            if (!e.end_time) return toast.error("End time cannot be empty");
        }

        const enriched = entries.map((entry) => ({
            ...entry,
            class_id: classId,
        }));

        setAdding(true);

        try {
            const token = localStorage.getItem("token");

            const response = await API.post("/timetable/add",
                {
                    is_portal: true,
                    day_of_week: dayOfWeek,
                    rotation_week: Number(rotationWeek),
                    entries: enriched
                },
                { headers: { token } }
            );

            if (response.data.response_code === 1000) {
                toast.success("Added successfully!");
                onAdded();
                loadExisting();
                resetForm();
            } else toast.error(response.data.message);

        } catch (err) {
            toast.error("Error adding timetable");
        } finally {
            setAdding(false);
        }
    };


    // Save edit row
    const saveEditedRow = async (id) => {
        const row = editingRows[id];
        if (!row) return;

        try {
            const token = localStorage.getItem("token");

            const res = await API.put(`/timetable/edit/${id}`, row, {
                headers: { token, is_portal: true }
            });

            if (res.data.response_code === 1000) {
                toast.success("Updated successfully");
                loadExisting();
                onAdded();
            } else {
                toast.error(res.data.message);
            }
        } catch (err) {
            toast.error("Update failed");
        }
    };

    // Delete
    const handleDelete = async (id) => {
        if (!window.confirm("Delete this slot?")) return;
        try {
            const token = localStorage.getItem("token");
            const res = await API.delete(`/timetable/delete/${id}?is_portal=true`, {
                headers: { token }
            });

            if (res.data.response_code === 1000) {
                toast.success("Deleted");
                loadExisting();
                onAdded();
            } else toast.error(res.data.message);

        } catch (err) {
            toast.error("Delete failed");
        }
    };

    const resetForm = () => {
        setEntries([{ subject_name: "", teacher_uuid: "", start_time: "", end_time: "", interval: 30, remarks: "" }]);
    };

    return (
        <Modal isOpen={isOpen} toggle={onClose} size="xl">
            <ModalHeader toggle={onClose}>Manage Timetable – {dayOfWeek}</ModalHeader>

            <ModalBody>

                {/* Existing Slots */}
                <h6 className="fw-bold mb-2">Existing Slots</h6>

                {existing.length === 0 ? (
                    <p className="text-muted mb-4">No entries yet.</p>
                ) : (
                    existing.map((item) => {
                        const row = editingRows[item.id];
                        return (
                            <div key={item.id} className="border rounded p-3 mb-3">

                                <div className="row g-2">

                                    <div className="col-md-4">
                                        <label>Subject</label>
                                        <Select
                                            options={subjects}
                                            value={subjects.find(s => s.value === row.subject_name) || null}
                                            onChange={(opt) =>
                                                setEditingRows({
                                                    ...editingRows,
                                                    [item.id]: { ...row, subject_name: opt?.value || "" }
                                                })
                                            }
                                        />
                                    </div>
                                    <div className="col-md-4">
                                        <label>Teacher</label>
                                        <Select
                                            options={teachers}
                                            value={
                                                teachers.find(
                                                    (t) => String(t.value).trim() === String(row.teacher_uuid).trim()
                                                ) || null
                                            }
                                            onChange={(opt) =>
                                                setEditingRows({
                                                    ...editingRows,
                                                    [item.id]: { ...row, teacher_uuid: opt?.value || "" }
                                                })
                                            }
                                            menuPortalTarget={document.body}
                                            menuPosition="fixed"
                                            styles={{
                                                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                                control: (base) => ({
                                                    ...base,
                                                    minHeight: "32px",
                                                    borderRadius: "8px",
                                                    fontSize: "13px"
                                                }),
                                            }}
                                        />

                                    </div>

                                    <div className="col-md-2">
                                        <label>Start</label>
                                        <input
                                            type="time"
                                            className="form-control"
                                            value={row.start_time}
                                            onChange={(e) =>
                                                setEditingRows({
                                                    ...editingRows,
                                                    [item.id]: { ...row, start_time: e.target.value }
                                                })
                                            }
                                        />
                                    </div>

                                    <div className="col-md-2">
                                        <label>End</label>
                                        <input
                                            type="time"
                                            className="form-control"
                                            value={row.end_time}
                                            onChange={(e) =>
                                                setEditingRows({
                                                    ...editingRows,
                                                    [item.id]: { ...row, end_time: e.target.value }
                                                })
                                            }
                                        />
                                    </div>

                                    <div className="col-md-12 mt-2 d-flex justify-content-between">
                                        <textarea
                                            className="form-control"
                                            rows={2}
                                            placeholder="Remarks"
                                            value={row.remarks || ""}
                                            onChange={(e) =>
                                                setEditingRows({
                                                    ...editingRows,
                                                    [item.id]: { ...row, remarks: e.target.value }
                                                })
                                            }
                                        />

                                        <div className="d-flex gap-2 ms-3">
                                            <button className="btn btn-success" onClick={() => saveEditedRow(item.id)}>
                                                Save
                                            </button>
                                            <button className="btn btn-outline-danger" onClick={() => handleDelete(item.id)}>
                                                Delete
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })
                )}
                <hr />

                {/* New Entry */}
                <h6 className="fw-bold mt-4">Add New Slot</h6>
                {entries.map((entry, idx) => (
                    <div key={idx} className="border rounded p-3 mb-3 shadow-sm">
                        <div className="row g-2">
                            <div className="col-md-4">
                                <label>Subject</label>
                                <Select
                                    options={subjects}
                                    value={subjects.find(s => s.value === entry.subject_name) || null}
                                    onChange={(opt) => {
                                        const upd = [...entries];
                                        upd[idx].subject_name = opt?.value || "";
                                        setEntries(upd);
                                    }}
                                />

                            </div>

                            <div className="col-md-4">
                                <label>Teacher</label>
                                <Select
                                    options={teachers}
                                    onChange={(opt) => {
                                        const upd = [...entries];
                                        upd[idx].teacher_uuid = opt?.value || "";
                                        setEntries(upd);
                                    }}
                                />
                            </div>

                            <div className="col-md-4">
                                <label>Interval (mins)</label>
                                <select
                                    className="form-control"
                                    value={entry.interval}
                                    onChange={(e) => {
                                        const val = Number(e.target.value);
                                        const upd = [...entries];
                                        upd[idx].interval = val;
                                        upd[idx].end_time = generateEndTime(upd[idx].start_time, val);
                                        setEntries(upd);
                                    }}
                                >
                                    {[15, 20, 25, 30, 35, 40, 45, 50, 60].map(i => (
                                        <option key={i} value={i}>{i}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="col-md-3 mt-2">
                                <label>Start</label>
                                <input
                                    type="time"
                                    className="form-control"
                                    value={entry.start_time}
                                    onChange={(e) => {
                                        const interval = entries[idx].interval;
                                        const snapped = snapToInterval(e.target.value, interval);
                                        const upd = [...entries];
                                        upd[idx].start_time = snapped;
                                        upd[idx].end_time = generateEndTime(snapped, interval);
                                        setEntries(upd);
                                    }}
                                />
                            </div>

                            <div className="col-md-3 mt-2">
                                <label>End</label>
                                <input
                                    type="time"
                                    className="form-control"
                                    value={entry.end_time}
                                    onChange={(e) => {
                                        const interval = entries[idx].interval;
                                        const upd = [...entries];
                                        upd[idx].end_time = snapToInterval(e.target.value, interval);
                                        setEntries(upd);
                                    }}
                                />
                            </div>

                            <div className="col-md-2 mt-4">
                                {entries.length > 1 && (
                                    <button
                                        className="btn btn-outline-danger"
                                        onClick={() => setEntries(entries.filter((_, i) => i !== idx))}
                                    >
                                        Delete
                                    </button>
                                )}
                            </div>

                        </div>
                    </div>
                ))}

                <div className="text-center">
                    <Button
                        color="success"
                        onClick={() =>
                            setEntries(prev => [
                                ...prev,
                                {
                                    subject_name: "",
                                    teacher_uuid: "",
                                    start_time: "",
                                    end_time: "",
                                    interval: prev[prev.length - 1].interval,
                                    remarks: "",
                                },
                            ])
                        }
                    >
                        + Add Another Slot
                    </Button>
                </div>

            </ModalBody>

            <ModalFooter>
                <Button color="primary" disabled={adding} onClick={handleSave}>
                    {adding ? "Saving..." : "Save Timetable"}
                </Button>
                <Button color="secondary" onClick={onClose}>
                    Close
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default AddTimetableModal;
